/**
 * PlanSummary Component
 *
 * Shows methods in plan with actions and i18n support
 */

import React from 'react';
import { usePlan } from '../contexts/PlanContext';
import { useLanguage } from '../contexts/LanguageContext';

export const PlanSummary: React.FC = () => {
  const { plan, removeMethod, clearPlan, exportPdf } = usePlan();
  const { t } = useLanguage();

  // Handle undefined or invalid plan data
  const safePlan = Array.isArray(plan) ? plan : [];

  const totalDurationMin = safePlan.reduce((sum, item) => sum + item.duration_min, 0);
  const totalDurationMax = safePlan.reduce((sum, item) => sum + item.duration_max, 0);

  if (safePlan.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <svg className="w-6 h-6 text-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h2 className="text-xl font-bold text-navy">{t('learningPlan')}</h2>
        </div>
        <p className="text-gray-600">{t('planEmpty')} {t('planEmptyHint')}</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <svg className="w-6 h-6 text-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          <h2 className="text-xl font-bold text-navy">
            {t('learningPlan')}
          </h2>
        </div>
        <span className="badge badge-navy text-sm" data-testid="plan-count">
          {safePlan.length} {safePlan.length === 1 ? t('method') : t('methods')}
        </span>
      </div>

      {/* Plan items */}
      <ul className="space-y-3 mb-6">
        {safePlan.map((item) => (
          <li
            key={item.id}
            className="bg-primary-light rounded-lg p-4 flex items-center justify-between gap-4 hover:bg-primary/30 transition-colors"
          >
            <div className="flex-1">
              <h3 className="font-semibold text-navy mb-1">{item.title}</h3>
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <svg className="w-4 h-4 text-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{item.duration_min}-{item.duration_max} min</span>
              </div>
            </div>

            {item.facilitator_material_url && (
              <a
                href={item.facilitator_material_url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${t('downloadMaterials')}: ${item.title}`}
                title={t('downloadMaterials')}
                className="text-teal hover:text-teal-dark focus:outline-none focus:ring-2 focus:ring-teal rounded-lg p-2 transition-colors flex-shrink-0"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
              </a>
            )}

            <button
              onClick={() => removeMethod(item.id)}
              aria-label={`Remove ${item.title} from plan`}
              className="text-red-600 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 rounded-lg p-2 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </li>
        ))}
      </ul>

      {/* Total duration */}
      <div className="bg-white rounded-lg p-4 mb-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <span className="text-navy font-semibold">{t('totalDuration')}:</span>
          <span className="text-teal font-bold text-lg">
            {totalDurationMin}-{totalDurationMax} {t('minutes')}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={exportPdf}
          className="btn-secondary flex-1"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          {t('exportPdf')}
        </button>

        <button
          onClick={clearPlan}
          className="px-6 py-3 bg-white text-red-600 border-2 border-red-600 font-medium rounded-lg hover:bg-red-600 hover:text-white focus:outline-none focus:ring-4 focus:ring-red-600/30 transition-all duration-200"
        >
          <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          {t('clearPlan')}
        </button>
      </div>
    </div>
  );
};
